import React, { CSSProperties } from "react";
import {
  addMinutes,
  startOfDay,
  endOfDay,
  isBefore,
  format,
  isSameHour,
  isSameMinute,
} from "date-fns";
import { parseTime, Time } from "./time_utils";
import { setSameDay, toTime } from "../lib/datetime";
import { Picker } from "./picker";

interface TimePickerProps {
  testID?: string;
  value?: Time;
  onChange?: (value: Time) => void;
  /** Minimum difference in minutes between time options. */
  step?: number;
  startTime?: Time | null;
  endTime?: Time | null;
  formatter?: (time: Time) => string;
  isBlocked?: (time: Time) => boolean;
  description?: string;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  override?: {
    root?: { style?: CSSProperties };
    view?: { style?: CSSProperties };
    arrow?: { style?: CSSProperties };
  };
}

/**
 * Control for selecting a parseTime
 */
export function TimePicker(props: TimePickerProps) {
  const {
    value,
    step = 60,
    placeholder,
    onChange = () => {},
    testID,
    startTime,
    endTime,
    formatter = (time: Time) => format(parseTime(time), "HH:mm"),
    isBlocked,
    description,
    disabled = false,
    invalid,
    override,
  } = props;

  const hours = getHours({
    disabled,
    formatter,
    step,
    startTime,
    endTime,
    isBlocked,
  });

  return (
    <Picker
      testID={testID}
      options={hours}
      disabled={disabled}
      description={description}
      invalid={invalid}
      value={value}
      placeholder={placeholder}
      onChange={onChange}
      override={{
        view: {
          style: {
            paddingLeft: 8,
            paddingRight: 8,
            borderRadius: 5,
            border: "1px solid #D7D5DD",
            ...override?.view?.style,
          },
        },
      }}
    />
  );
}
interface HourOption {
  value: string;
  label: string;
  disabled: boolean;
}

const referenceDate = new Date();

export function getHours(params: {
  step?: number;
  startTime?: Time | null;
  endTime?: Time | null;
  formatter: (time: Time) => string;
  isBlocked?: (time: Time) => boolean;
  disabled?: boolean;
}): HourOption[] {
  const {
    step = 30,
    isBlocked = () => false,
    startTime,
    endTime,
    formatter,
    disabled,
  } = params;

  const hours: HourOption[] = [];

  if (disabled === true) {
    return hours;
  }

  const start = startTime
    ? setSameDay(parseTime(startTime), referenceDate)
    : startOfDay(referenceDate);

  const end = endTime
    ? setSameDay(parseTime(endTime), referenceDate)
    : endOfDay(referenceDate);

  let current = start;

  while (
    isBefore(current, end) ||
    (isSameHour(current, end) && isSameMinute(current, end))
  ) {
    hours.push({
      value: toTime(current),
      label: formatter(toTime(current)),
      disabled: isBlocked(toTime(current)),
    });

    const next = addMinutes(current, step);

    current = next;
  }

  return hours;
}
